import { useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { Category, ErrorAlert } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

const STALE_MS = 5 * 60 * 1000; // matches the server's `category-catalog` cache TTL (300s)

export interface UseCategoryCatalogResult {
  categories: readonly Category[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * `category.list` (F31) — the full category catalog, source of truth for
 * `CategoryFilterChips`. NEVER derive the chip set from `search.query`'s
 * currently-loaded result rows: that facet self-eliminates (selecting a
 * category narrows `rows`, which then narrows the chip set built from those
 * same `rows`) and drops any category with zero hits on the loaded page.
 * Non-critical enrichment (§16 "fail-open... missing thumbnail →
 * placeholder") — on error/loading this just yields an empty chip list
 * rather than blocking or erroring the whole search page, which owns its
 * own loading/error states for the primary result list.
 */
export function useCategoryCatalog(): UseCategoryCatalogResult {
  const { wsClient, connectionState } = useWs();

  const query = useQuery<{ categories: Category[] }, ErrorAlert>({
    queryKey: ['category', 'list'],
    queryFn: () => requestTyped(wsClient, 'category.list', undefined),
    enabled: connectionState === 'connected',
    staleTime: STALE_MS,
  });

  return {
    categories: query.data?.categories ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
